import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { IshipAddres, UserState } from '../../components/interfaces';
import { 
  fetchRegisterUser, 
  fetchLoginUser, 
  fetchCurrentUser,
  fetchUserShip,
  createUserShip,
  logoutUser,
  fetchResetPasswordEmail,
  fetchResetPasswordByToken,
  updateShippingAddressCheckBox,
  updateShippingAddress,
  updateEmailPreference,
  updateUserAvatar,
  removeUserAvatar,
} from '../../services';
import { message } from 'antd';

const initialState: UserState = {
  user: null,
  isAuthenticated: false,
  authChecked: false,
  loading: false,
  error: null,
  shipping: null,
  selectedAddress: 0,
};

export const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    setSelectedAddress(state, action: PayloadAction<number>) {
      state.selectedAddress = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
    .addCase(fetchRegisterUser.pending, (state) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(fetchRegisterUser.fulfilled, (state, action) => {
      state.loading = false;
      state.user = action.payload ?? null;
      state.isAuthenticated = false;
    })
    .addCase(fetchRegisterUser.rejected, (state, action) => {
      state.loading = false;
      state.error = (action.payload as string) ?? 'Failed to register user';
    })
    .addCase(fetchLoginUser.pending, (state) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(fetchLoginUser.fulfilled, (state, action) => {
      state.loading = false;
      state.isAuthenticated = true;
      state.authChecked = true;
      state.user = action.payload ?? null;  
    })
    .addCase(fetchLoginUser.rejected, (state, action) => {
      state.loading = false;
      state.error = (action.payload as string) ?? 'Failed to login';
    })
    .addCase(fetchCurrentUser.pending, (state) => {
      state.loading = true;
    })
    .addCase(fetchCurrentUser.fulfilled, (state, action) => {
      state.loading = false;
      state.isAuthenticated = true;
      state.authChecked = true;
      state.user = action.payload.user;  
    })
    .addCase(fetchCurrentUser.rejected, (state, action) => {
      state.loading = false;
      state.isAuthenticated = false;
      state.authChecked = true;
    
      const message = typeof action.payload === 'string' ? action.payload : action.payload?.message;
      if (message && message !== 'No user found') {
        state.error = message;
      } else {
        state.error = null;
      }
    
      state.user = null;
    })
    .addCase(createUserShip.pending, (state) => {
      state.loading = true;
    })
    .addCase(createUserShip.fulfilled, (state, action) => {
      const newAddress = action.payload as unknown as IshipAddres;
      if (newAddress) {
        state.shipping = newAddress;
      }
      state.loading = false;
    })
    .addCase(createUserShip.rejected, (state, action) => {
        state.loading = false;
        console.error('Failed to create shipping address:', action.payload);
    })
    .addCase(fetchUserShip.pending, (state) => {
      state.loading = true;
    })
    .addCase(fetchUserShip.fulfilled, (state, action) => {
      state.loading = false;
      state.shipping = action.payload; 
    })
    .addCase(fetchUserShip.rejected, (state, action) => {
        state.error = action.payload ?? null;
        state.loading = false;
        state.shipping = {
            id: '',
            firstName: '',
            lastName: '',
            addressLine1: '',
            addressLine2: '',
            city: '',
            postcode: '',
            country: '',
            province: '',
            telephone: '',
            phoneCode: '',
            emoji: '',
            useAddressForBilling: false,
        };
    })
    .addCase(logoutUser.fulfilled, (state) => {
      state.user = null;
      state.isAuthenticated = false;
      state.authChecked = true;
      state.loading = false;
      state.error = null;
      state.shipping = null;
      message.success('Logged out successfully!');
    })
    .addCase(fetchResetPasswordEmail.pending, (state) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(fetchResetPasswordEmail.fulfilled, (state) => {
      state.loading = false;
    })
    .addCase(fetchResetPasswordEmail.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    })
    .addCase(fetchResetPasswordByToken.pending, (state) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(fetchResetPasswordByToken.fulfilled, (state) => {
      state.loading = false;
      message.success('Password has been successfully reset!');
    })
    .addCase(fetchResetPasswordByToken.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    })
    .addCase(updateShippingAddress.pending, (state) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(updateShippingAddress.fulfilled, (state, action) => {
      state.loading = false;
      state.shipping = action.payload.updateShippingAddress;
    })
    .addCase(updateShippingAddress.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload || action.error.message || 'Failed to update shipping address';
    })
    .addCase(updateShippingAddressCheckBox.pending, (state) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(updateShippingAddressCheckBox.fulfilled, (state, action) => {
      state.loading = false;
      if (state.shipping) {
        state.shipping = {
          ...state.shipping,
          useAddressForBilling: action.payload.updateShippingAddress.useAddressForBilling,
        };
      }
    })
    .addCase(updateShippingAddressCheckBox.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload ?? null;
      message.error('Error updating shipping address.');
    })
    .addCase(updateEmailPreference.pending, (state) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(updateEmailPreference.fulfilled, (state, action) => {
      state.loading = false;
      if (state.user) {
        state.user = {
          ...state.user,
          marketingEmailsOptIn: action.payload.marketingEmailsOptIn,
        };
      }
    })
    .addCase(updateEmailPreference.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload ?? 'Failed to update email preferences';
    })
    .addCase(updateUserAvatar.pending, (state) => {
      state.error = null;
    })
    .addCase(updateUserAvatar.fulfilled, (state, action) => {
      if (state.user) {
        state.user.avatar = action.payload.avatar;
      }
    })
    .addCase(updateUserAvatar.rejected, (state, action) => {
      state.error = action.payload ?? 'Failed to upload profile photo.';
    })
    .addCase(removeUserAvatar.fulfilled, (state) => {
      if (state.user) {
        state.user.avatar = null;
      }
    })
    .addCase(removeUserAvatar.rejected, (state, action) => {
      state.error = action.payload ?? 'Failed to remove profile photo.';
    });
  },
});

export const { setError, setSelectedAddress } = userSlice.actions;
export default userSlice.reducer;
